import { ITransactionDetailInstruction } from 'interfaces/tx.interface'
import { PublicKey } from '@bbachain/web3.js'
import { capitalCase } from 'text-case'

export interface CustomInstructionProps {
	label: string
	content: string
	type: 'link' | 'balance' | 'date' | 'text'
}

const isValidBBAPublicKey = (address: string) => {
	try {
		const publicKey = new PublicKey(address)
		return PublicKey.isOnCurve(publicKey.toBuffer()) // Check if it's a valid Ed25519 key
	} catch (error) {
		return false
	}
}

const mapInfoToCustomProps = (info: Record<string, any>): CustomInstructionProps[] => {
	return Object.entries(info).map(([key, value]) => ({
		label: capitalCase(key),
		content: String(value),
		type: isValidBBAPublicKey(String(value)) ? 'link' : 'text'
	}))
}

export const instructionMapper = (data: ITransactionDetailInstruction): CustomInstructionProps[] => {
	const program = data.program
	const type = data.parsed.type

	if (program === 'vote') {
		switch (type) {
			case 'vote':
				return [
					{
						label: 'Clock Sysvar',
						content: data.parsed.info.clockSysvar,
						type: 'link'
					},
					{
						label: 'Slot Hashes Sysvar',
						content: data.parsed.info.slotHashesSysvar,
						type: 'link'
					},
					{
						label: 'Vote Hash',
						content: data.parsed.info.vote.hash,
						type: 'text'
					},
					{
						label: 'Timestamp',
						content: data.parsed.info.vote.timestamp,
						type: 'date'
					},
					{
						label: 'Slots',
						content: data.parsed.info.vote.slots,
						type: 'text'
					},
					{
						label: 'Vote Account',
						content: data.parsed.info.voteAccount,
						type: 'link'
					},
					{
						label: 'Vote Authority',
						content: data.parsed.info.voteAuthority,
						type: 'link'
					}
				]
			default:
				return mapInfoToCustomProps(data.parsed.info)
		}
	} else if (program === 'system') {
		switch (type) {
			case 'transfer':
				return [
					{
						label: 'From Address',
						content: data.parsed.info.source,
						type: 'link'
					},
					{
						label: 'To Address',
						content: data.parsed.info.destination,
						type: 'link'
					},
					{
						label: 'Transfer Amount (BBA)	',
						content: data.parsed.info.daltons,
						type: 'balance'
					}
				]
			case 'allocate':
				return [
					{
						label: 'Account Address',
						content: data.parsed.info.account,
						type: 'link'
					},
					{
						label: 'Allocated Data Size	',
						content: `${data.parsed.info.space} byte(s)`,
						type: 'text'
					}
				]
			case 'assign':
				return [
					{
						label: 'Account Address',
						content: data.parsed.info.account,
						type: 'link'
					},
					{
						label: 'Assigned Program Id',
						content: data.parsed.info.owner,
						type: 'link'
					}
				]
			default:
				return mapInfoToCustomProps(data.parsed.info)
		}
	} else if (program === 'stake') {
		switch (type) {
			case 'delegate':
				return [
					{
						label: 'Stake Address',
						content: data.parsed.info.stakeAccount,
						type: 'link'
					},
					{
						label: 'Delegated Vote Address',
						content: data.parsed.info.voteAccount,
						type: 'link'
					},
					{
						label: 'Authority Address',
						content: data.parsed.info.stakeAuthority,
						type: 'link'
					}
				]
			default:
				return mapInfoToCustomProps(data.parsed.info)
		}
	} else {
		return [
			{
				label: 'Unknown State',
				content: 'Unknown State',
				type: 'text'
			}
		]
	}
}
